/**
 * parsePreview — client-side preview parse for the New Import wizard (FR-03.4/5).
 *
 * Reads the SELECTED file in the browser to render the preview (first 5 rows +
 * total count) and the auto-detected column mapping (mapped ✓ / stored ⓘ). This
 * is preview-only — the AUTHORITATIVE parse runs server-side in `import_worker`
 * (this is intentionally a light mirror of `baby_chops_v1` so the recruiter sees
 * what will be imported before committing).
 *
 * Supports `.csv` (header + rows) and `.json` (array of profile objects).
 */

// The baby_chops_v1 recognized columns (mirror of the backend domain catalog).
const PROFILE_COLUMNS = [
  "First Name",
  "Last Name",
  "Headline",
  "Location",
  "LinkedIn URL",
  "Profile Image URL",
  "Connection Degree",
  "Summary",
];
const JOB_SUFFIXES = [
  "Title",
  "Company",
  "Company ID",
  "Company URL",
  "Company LinkedIn URL",
  "Start",
  "End",
  "Location",
  "Description",
];
const MAX_JOBS = 10;

export function babyChopsV1StandardColumns(): string[] {
  const cols = [...PROFILE_COLUMNS];
  for (let n = 1; n <= MAX_JOBS; n++) {
    for (const s of JOB_SUFFIXES) cols.push(`Job ${n} ${s}`);
  }
  return cols;
}

/**
 * The ChopLink KEY columns — what the upload copy names and what
 * `mapping.missing` reports when absent. NOT the full standard catalog above:
 * "Missing: Job 7 Description" on a 3-job file would be noise. The server's
 * gate (`nu_assist_shared.imports.viability`) requires `LinkedIn URL` plus a
 * name column; the two job columns are here because a header without them is
 * not a ChopLink export either, even if it would pass the gate.
 */
const KEY_COLUMNS = ["First Name", "Last Name", "LinkedIn URL", "Job 1 Title", "Job 1 Company"];
const KEY_COLUMNS_JSON = ["firstName", "lastName", "linkedinUrl", "positions"];

const LINKEDIN_URL_COLUMN = "LinkedIn URL";
const LINKEDIN_URL_KEY = "linkedinUrl";

/** BOM + padding + case, comparison only — the same widening the server's
 *  `normalize_header` does, so the preview recognises what the gate will. */
function normalizeHeader(name: string): string {
  return name
    .replace(/^\ufeff/, "")
    .trim()
    .toLowerCase();
}

/** The value under `key` on a JSON profile, the key matched the way the
 *  worker's `_NormalizedRow` matches it (exact first, then normalized).
 *  `undefined` when no key of the object normalizes to it. */
function jsonValueByNormalizedKey(profile: Record<string, unknown>, key: string): unknown {
  if (Object.hasOwn(profile, key)) return profile[key];
  const want = normalizeHeader(key);
  const found = Object.keys(profile).find((k) => normalizeHeader(k) === want);
  return found === undefined ? undefined : profile[found];
}

/**
 * Does this cell hold a LinkedIn PROFILE URL — one the worker will keep?
 *
 * A DELIBERATE TypeScript twin of `normalize_linkedin_url` (python-shared),
 * reduced to its accept/reject decision: the host must be linkedin.com and
 * the path must start with `/in/<handle>`. Sales Navigator (`/sales/lead/…`),
 * legacy `/pub/…`, company pages and blanks all fail it, and the worker drops
 * exactly those rows.
 *
 * `packages/extension/src/content/linkedin/recruiter/recruiter-url.ts` says
 * this epic "must not grow a TypeScript twin" of that function, and that
 * constraint is OVERRIDDEN here, on the record (/pr-review #971 iter 1,
 * Agent C I2): the preview has to predict the worker's count BEFORE the
 * upload, or the recruiter commits to a number that is not the one that
 * lands. What keeps the twin honest is not a comment but
 * `__tests__/parsePreview.parity.test.ts`: the same table of URLs the Python
 * side pins in `packages/python-shared/tests/test_identity.py`
 * (`test_normalize_linkedin_url_variants_collapse_to_one_key` /
 * `..._returns_none_for_unmatchable`). CHANGE THE TWO TOGETHER — a URL the
 * server starts accepting or rejecting is a row added to both tables.
 *
 * Exported for that test only.
 */
export function isProfileUrl(value: string | undefined): boolean {
  const raw = (value ?? "").trim();
  if (!raw) return false;
  const noScheme = raw.replace(/^[a-z]+:\/\//i, "").split(/[?#]/, 1)[0];
  const slash = noScheme.indexOf("/");
  const host = (slash === -1 ? noScheme : noScheme.slice(0, slash)).toLowerCase();
  const path = slash === -1 ? "" : noScheme.slice(slash + 1);
  if (!host.includes("linkedin.com")) return false;
  const segments = path.split("/").filter((seg) => seg.length > 0);
  return segments.length >= 2 && segments[0].toLowerCase() === "in";
}

// JSON camelCase keys map to the same recognized profile concepts.
const JSON_PROFILE_KEYS = new Set([
  "firstName",
  "lastName",
  "headline",
  "location",
  "linkedinUrl",
  "profileImageUrl",
  "connectionDegree",
  "summary",
  "positions",
]);

export interface ColumnMapping {
  /** Recognized columns (rendered "mapped ✓"). */
  mapped: string[];
  /** Unrecognized columns (rendered "stored ⓘ" — kept, not dropped). */
  stored: string[];
  /** ChopLink KEY columns absent from the header (rendered "Missing: …").
   *  Informational: a header without `LinkedIn URL` is refused at `/complete`
   *  and never reaches the mapping step; what lands here is a key column a
   *  file that PASSED happens to lack. */
  missing: string[];
  allStandardMapped: boolean;
}

export interface PreviewData {
  /** Column headers in source order. */
  columns: string[];
  /** First-5 rows as {column → cell} maps. */
  rows: Array<Record<string, string>>;
  /** Total contact count in the file (rows / JSON list length). */
  total: number;
  mapping: ColumnMapping;
  kind: "csv" | "json";
  /** Rows the worker WILL drop: LinkedIn URL empty or not a `/in/` profile.
   *  Counted over the whole file, not the previewed five, so the number the
   *  recruiter commits to is the number that will land. `0` when
   *  `hasUrlColumn` is false: the row rule is only evaluated on a file that
   *  HAS the column. */
  skippedNoLinkedin: number;
  /** Whether the header carries `LinkedIn URL` (`linkedinUrl` on the first
   *  JSON object), found the way the server gate finds it: through the same
   *  BOM/case/padding normalization on both branches. A file WITHOUT it
   *  is refused at `/complete` with the header reason ("not a ChopLink
   *  export: it has no `LinkedIn URL` column"), so the wizard must let it
   *  through to be told that — the nothing-to-import gate reads this flag and
   *  stays out of the way. Counting every row of such a file as "URL-less"
   *  (what the first version did) disabled the CTA and told the recruiter to
   *  re-capture profiles a file with a `Profile URL` column already had
   *  (/pr-review #971 iter 2, Agent A section 2.1 / Agent C C2). */
  hasUrlColumn: boolean;
}

export class PreviewParseError extends Error {}

function classify(columns: string[], standard: Set<string>, key: string[]): ColumnMapping {
  const mapped: string[] = [];
  const stored: string[] = [];
  for (const c of columns) (standard.has(c) ? mapped : stored).push(c);
  // `mapped`/`stored` keep the exact-match rule the catalog has always used;
  // `missing` is judged through the same normalization the server gate
  // applies, or a BOM-headed file would list `First Name` as missing while
  // the gate lets it through.
  const present = new Set(columns.map(normalizeHeader));
  const missing = key.filter((k) => !present.has(normalizeHeader(k)));
  return { mapped, stored, missing, allStandardMapped: stored.length === 0 };
}

/** Minimal CSV line splitter (handles simple quoted fields with commas). */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

// LINE-SPLIT ON PURPOSE, AND THEREFORE NOT THE WORKER'S PARSER. A quoted cell
// holding a line break (a wrapped `Summary`) becomes two "rows" here and one
// row there, so `total` and `skippedNoLinkedin` can each run HIGH by one per
// such cell; the worker (`csv.DictReader`) is authoritative. ChopLink writes
// `Summary` quoted but single-line in practice, so this is a known, bounded
// mismatch, not a server bug to "fix" by making the worker agree with the
// preview (/pr-review #971 iter 1, Agent A section 3).
function parseCsv(text: string): PreviewData {
  const lines = text.split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length === 0) throw new PreviewParseError("Empty CSV file.");
  const columns = splitCsvLine(lines[0]).map((c) => c.trim());
  const dataLines = lines.slice(1);
  const rows = dataLines.slice(0, 5).map((line) => {
    const cells = splitCsvLine(line);
    const row: Record<string, string> = {};
    columns.forEach((col, i) => {
      row[col] = (cells[i] ?? "").trim();
    });
    return row;
  });
  // The URL column, found the way the gate finds it (BOM/case/padding
  // tolerant). ABSENT COLUMN: the rows are not counted. That file is judged
  // by its HEADER at `/complete` (422, names the missing column and the
  // ChopLink columns); the row rule never runs on it, and neither does this
  // count — see `hasUrlColumn`.
  const urlIndex = columns.findIndex(
    (c) => normalizeHeader(c) === normalizeHeader(LINKEDIN_URL_COLUMN)
  );
  const hasUrlColumn = urlIndex !== -1;
  let skippedNoLinkedin = 0;
  if (hasUrlColumn) {
    for (const line of dataLines) {
      if (!isProfileUrl(splitCsvLine(line)[urlIndex])) skippedNoLinkedin += 1;
    }
  }
  return {
    columns,
    rows,
    total: dataLines.length,
    mapping: classify(columns, new Set(babyChopsV1StandardColumns()), KEY_COLUMNS),
    kind: "csv",
    skippedNoLinkedin,
    hasUrlColumn,
  };
}

function parseJson(text: string): PreviewData {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new PreviewParseError("Invalid JSON file.");
  }
  if (!Array.isArray(data)) {
    throw new PreviewParseError("Expected a JSON array of profiles.");
  }
  const first = (data[0] ?? {}) as Record<string, unknown>;
  const columns = Object.keys(first);
  const rows = (data.slice(0, 5) as Array<Record<string, unknown>>).map((p) => {
    const row: Record<string, string> = {};
    for (const k of columns) {
      const v = p[k];
      row[k] = v == null ? "" : Array.isArray(v) ? `${v.length} positions` : String(v);
    }
    return row;
  });
  // The key is judged on the FIRST object, as the server gate judges a JSON
  // header (`_json_header` reads the first object's keys, `assess_viability`
  // then matches them through `normalize_header`), so the lookup is by
  // NORMALIZED key here too — a hand-edited capture whose key reads
  // `LinkedinUrl` passes the server gate and parses in the worker (its
  // `_NormalizedRow` reads every profile the same way), and an exact-key test
  // here would count nothing for it and gate nothing (/pr-review #971 iter 3,
  // Agent C M12). A later object that lacks the key is a row the worker
  // drops, counted below. Same absent-column rule as the CSV branch.
  const hasUrlColumn = jsonValueByNormalizedKey(first, LINKEDIN_URL_KEY) !== undefined;
  let skippedNoLinkedin = 0;
  if (hasUrlColumn) {
    for (const profile of data as Array<Record<string, unknown>>) {
      const url = jsonValueByNormalizedKey(profile ?? {}, LINKEDIN_URL_KEY);
      if (!isProfileUrl(typeof url === "string" ? url : undefined)) skippedNoLinkedin += 1;
    }
  }
  return {
    columns,
    rows,
    total: data.length,
    mapping: classify(columns, JSON_PROFILE_KEYS, KEY_COLUMNS_JSON),
    kind: "json",
    skippedNoLinkedin,
    hasUrlColumn,
  };
}

/**
 * The wizard's reason for a file the row rule would EMPTY — every row of a
 * file that HAS the `LinkedIn URL` column lacks a `/in/` profile URL.
 *
 * THE SAME TEXT, WORD FOR WORD, AS `import_worker.domain.all_rows_skipped_reason`
 * (`packages/workers/import_worker/import_worker/domain.py`): the worker ends
 * such an import `failed` with it when a path this wizard does not front
 * (the extension's capture) uploads one. One rule, one sentence, read in two
 * places; change the two together (/pr-review #971 iter 2, Agent C M8).
 */
export function allRowsSkippedReason(skipped: number): string {
  const rows = skipped === 1 ? "its only row carries no" : `none of its ${skipped} rows carries a`;
  return `No rows to import: ${rows} LinkedIn profile URL (\`linkedin.com/in/...\`). A people list needs one per row; ChopLink writes it as \`LinkedIn URL\`. Re-capture the profiles with ChopLink and upload that file.`;
}

/** Parse the selected file's text into preview data (first 5 rows + mapping). */
export function parsePreviewText(filename: string, text: string): PreviewData {
  return filename.toLowerCase().endsWith(".json") ? parseJson(text) : parseCsv(text);
}

/** Read a File and parse it for preview (browser FileReader). */
export async function parsePreviewFile(file: File): Promise<PreviewData> {
  const text = await file.text();
  return parsePreviewText(file.name, text);
}
